import React from "react";
import { Box, Typography } from "@mui/material";
import type { GroupedBarFooterData } from "@hooks/use-grouped-bar-footer";

interface GroupedBarFooterProps {
  data: GroupedBarFooterData;
}

/**
 * Grouped bar chart footer (weight, time period, rolling average, visualization
 * base, and statistical-testing context), following the Bar Chart footer
 * conventions. Rendered from the values derived in useGroupedBarFooter.
 */
const GroupedBarFooter: React.FC<GroupedBarFooterProps> = ({ data }) => {
  const {
    dashboardFilterNote,
    timePeriodFooterText,
    rollingAverageComparisonFooterText,
    missingDataWarning,
    weight,
    weightsApplied,
    showChartN,
    chartNValue,
    baseName,
    comparisonRollingLabel,
    rollingPeriodsWarningMessage,
    statTestingNote,
  } = data;

  // Always render the footer shell so the dashboard-filter note (e.g. "No
  // complete quarter available…") still explains a suppressed state. The
  // stat-testing note is already null from the hook when suppressed.
  return (
    <>
      <Box
        sx={{
          mt: 1,
          p: 1,
          px: 2.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {baseName && (
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Base: {baseName}
            </Typography>
          )}
          {dashboardFilterNote && (
            <Typography
              variant="caption"
              sx={{ color: "#1976d2" }}
              fontWeight={600}
            >
              {dashboardFilterNote}
            </Typography>
          )}
          {!dashboardFilterNote && timePeriodFooterText && (
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {timePeriodFooterText}
            </Typography>
          )}
          {rollingAverageComparisonFooterText && (
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {rollingAverageComparisonFooterText}
            </Typography>
          )}
          {comparisonRollingLabel && (
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {comparisonRollingLabel}
            </Typography>
          )}
          {rollingPeriodsWarningMessage && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {rollingPeriodsWarningMessage}
            </Typography>
          )}
          {missingDataWarning && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {missingDataWarning}
            </Typography>
          )}
          {showChartN && chartNValue != null && (
            <Typography
              component="figcaption"
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              Chart N: {chartNValue.toLocaleString()}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Weight:{" "}
          {weight === "DISABLED"
            ? "No Weight"
            : weightsApplied
              ? "Default Weight"
              : "No Weight"}
        </Typography>
      </Box>

      {statTestingNote &&
        (statTestingNote.hasSignificantChanges ? (
          <Box
            sx={{
              mt: 1,
              p: 1,
              px: 2.5,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <span className="text-[#10B981]">▲</span> /{" "}
              <span className="text-[#EF4444]">▼</span>{" "}
              {statTestingNote.comparisonLabel}: Significant increase/decrease
              compared to {statTestingNote.comparisonDescription} at{" "}
              {statTestingNote.significanceLevel} level of significance.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 1,
              p: 1,
              px: 2.5,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No statistically significant differences found compared to{" "}
              {statTestingNote.comparisonDescription} at{" "}
              {statTestingNote.significanceLevel} level of significance.
            </Typography>
          </Box>
        ))}
    </>
  );
};

export default GroupedBarFooter;
